<template>
  <div class="message-wrapper" :class="messageClasses">
    <!-- System Notification -->
    <div v-if="message.type === 'system'" class="message-system">
      <div class="system-message">
        <v-icon size="16" color="#9E9E9E">mdi-information-outline</v-icon>
        <span>{{ message.contents }}</span>
      </div>
    </div>

    <!-- Support message with avatar (left-aligned in RTL) -->
    <div v-else-if="!IsMeSend" class="message-container message-support-container">
      <div v-if="showAvatar" class="message-avatar">
        <v-icon color="#5B7CFA" size="18">mdi-headset</v-icon>
      </div>
      <div v-else class="message-avatar-spacer"></div>
      <div class="message-content-wrapper">
        <div class="message-bubble message-bubble-support" :class="{ 'message-bubble-grouped': isGrouped }">
          <div v-if="message.type === 'bot'" class="message-bot-indicator">
            <v-icon size="12" color="#9E9E9E">mdi-robot</v-icon>
            <span>پاسخ خودکار</span>
          </div>
          <div class="message-text" v-html="formatMessageWithLinks(message.contents)"></div>
          <!-- Rich media preview -->
          <div v-if="message.media" class="message-media">
            <img v-if="message.media.type === 'image'" :src="message.media.url" :alt="message.media.alt || 'Image'" />
            <a v-else-if="message.media.type === 'link'" :href="message.media.url" target="_blank" class="link-preview">
              <div class="link-preview-content">
                <div class="link-preview-title">{{ message.media.title || message.media.url }}</div>
                <div v-if="message.media.description" class="link-preview-description">{{ message.media.description }}</div>
                <div class="link-preview-url">{{ message.media.url }}</div>
              </div>
            </a>
          </div>
          <MessageFooter
            :formatted-time="formattedTime"
            :show-timestamp="showTimestamp"
            :timestamp-title="message.date || formattedTime"
            :is-user="false"
          />
        </div>
      </div>
    </div>

    <!-- User message (right-aligned in RTL): refined bubble, timestamp row, checkmark -->
    <div v-else class="message-container message-user-container">
      <div class="message-content-wrapper message-content-wrapper--user">
        <div
          class="message-bubble message-bubble-user"
          :class="{ 'message-bubble-grouped': isGrouped }"
        >
          <div class="message-text message-text--user" v-html="formatMessageWithLinks(message.contents)"></div>
          <!-- Rich media preview -->
          <div v-if="message.media" class="message-media">
            <img
              v-if="message.media.type === 'image'"
              :src="message.media.lazyUrl || message.media.url"
              :data-src="message.media.url"
              :alt="message.media.alt || 'Image'"
              class="lazy-image"
              loading="lazy"
              @load="handleImageLoad"
              @error="handleImageError"
            />
          </div>
          <MessageFooter
            :formatted-time="formattedTime"
            :show-timestamp="showTimestamp"
            :timestamp-title="timestampTitle"
            :status="message.status || 'sent'"
            :is-user="true"
            @retry="handleRetry"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MessageFooter from './MessageFooter.vue';

export default {
  components: { MessageFooter },
  props: {
    IsMeSend: Boolean,
    message: {
      type: Object,
      required: true,
      default: () => ({
        contents: '',
        date: '',
        status: 'sent', // sent, delivered, read
        id: null,
        type: 'text', // text, system, bot
        media: null,
        ts: null
      })
    },
    isBookmarked: {
      type: Boolean,
      default: false
    },
    showAvatar: {
      type: Boolean,
      default: true
    },
    isGrouped: {
      type: Boolean,
      default: false
    },
    showTimestamp: {
      type: Boolean,
      default: true
    },
    isRecent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageObserver: null
    }
  },
  mounted() {
    // Lazy load images using Intersection Observer
    if ('IntersectionObserver' in window) {
      this.imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            this.imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });

      this.$nextTick(() => {
        const lazyImages = this.$el.querySelectorAll('.lazy-image[data-src]');
        lazyImages.forEach(img => this.imageObserver.observe(img));
      });
    }
  },
  beforeDestroy() {
    if (this.imageObserver) {
      this.imageObserver.disconnect();
    }
  },
  computed: {
    messageClasses() {
      return {
        'message-user': this.IsMeSend,
        'message-support': !this.IsMeSend,
        'message-grouped': this.isGrouped,
        'message-system': this.message.type === 'system'
      };
    },
    formattedTime() {
      if (!this.message.date) return '';
      // Extract time from date string (format: jYYYY/jMM/jDD HH:mm)
      const timeMatch = this.message.date.match(/(\d{2}:\d{2})/);
      return timeMatch ? timeMatch[1] : this.message.date;
    },
    /** Full timestamp tooltip for accessibility (e.g. "Sent: 09:44 on 1404/11/27") */
    timestampTitle() {
      const statusLabels = { sent: 'Sent', delivered: 'Delivered', read: 'Read', sending: 'Sending', failed: 'Failed' };
      const status = this.message.status || 'sent';
      const label = statusLabels[status] || statusLabels.sent;
      const dateStr = this.message.date || this.formattedTime;
      if (!dateStr) return '';
      return `${label}: ${dateStr}`;
    },
  },
  methods: {
    toggleBookmark() {
      this.$emit('bookmark', {
        messageId: this.message.id,
        isBookmarked: !this.isBookmarked
      });
    },
    handleEdit() {
      this.$emit('edit', {
        messageId: this.message.id,
        content: this.message.contents
      });
    },
    handleDelete() {
      if (confirm('آیا مطمئن هستید که می‌خواهید این پیام را حذف کنید؟')) {
        this.$emit('delete', {
          messageId: this.message.id
        });
      }
    },
    handleRetry() {
      this.$emit('retry', {
        messageId: this.message.id
      });
    },
    handleImageLoad(event) {
      event.target.classList.add('loaded');
    },
    handleImageError(event) {
      event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3Eخطا در بارگذاری تصویر%3C/text%3E%3C/svg%3E';
    },
    /**
     * Converts URLs in message text to clickable links
     * @param {string} text - The message text
     * @returns {string} - HTML string with links
     */
    formatMessageWithLinks(text) {
      if (!text || typeof text !== 'string') return '';
      
      // Escape HTML to prevent XSS
      const escapeHtml = (str) => {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      };
      
      // Determine link color based on message type
      const linkColor = this.IsMeSend ? '#81C784' : '#4CAF50';
      const linkHoverColor = this.IsMeSend ? '#A5D6A7' : '#45A049';
      
      // URL regex pattern - matches http, https, www, and common domains
      const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/gi;
      
      // Split text by URLs and convert URLs to links
      const parts = [];
      let lastIndex = 0;
      let match;
      
      while ((match = urlRegex.exec(text)) !== null) {
        // Add text before the URL
        if (match.index > lastIndex) {
          parts.push(escapeHtml(text.substring(lastIndex, match.index)));
        }
        
        // Process the URL
        let url = match[0];
        let href = url;
        
        // Add protocol if missing
        if (!url.match(/^https?:\/\//i)) {
          href = 'https://' + url;
        }
        
        // Remove trailing slash from display URL (but keep it in href)
        let displayUrl = url;
        if (displayUrl.endsWith('/')) {
          displayUrl = displayUrl.slice(0, -1);
        }
        
        // Escape the URL for display but keep href functional
        displayUrl = escapeHtml(displayUrl);
        parts.push(`<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" class="message-link" style="color: ${linkColor} !important; text-decoration: underline;" onmouseover="this.style.color='${linkHoverColor}'" onmouseout="this.style.color='${linkColor}'">${displayUrl}</a>`);
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(escapeHtml(text.substring(lastIndex)));
      }
      
      // If no URLs found, return escaped text
      if (parts.length === 0) {
        return escapeHtml(text);
      }
      
      return parts.join('');
    }
  }
}
</script>

<style scoped>
.message-wrapper {
  margin-bottom: 0;
  animation: messageSlideIn 0.3s ease-out;
}

.message-wrapper:not(.message-grouped) {
  margin-bottom: 0;
}

.message-wrapper.message-grouped {
  margin-bottom: 0;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-container {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  max-width: 100%;
}

.message-support-container {
  justify-content: flex-start;
}

.message-user-container {
  justify-content: flex-end;
  margin-left: auto;
  margin-right: 0;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #E8EDFF 0%, #D6E0FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.15);
  border: 2px solid rgba(102, 126, 234, 0.1);
}

.message-avatar-spacer {
  width: 32px;
  flex-shrink: 0;
}

.message-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  max-width: 70%;
}

.message-content-wrapper--user {
  max-width: 70%;
}

.message-bubble {
  padding: 14px;
  border-radius: 16px;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  position: relative;
  animation: bubbleAppear 0.25s ease-out;
}

.message-text--user {
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  word-break: break-word;
  direction: rtl;
}

.message-text--user a,
.message-text--user .message-link {
  color: #81C784 !important;
}

@keyframes bubbleAppear {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.message-bubble-support {
  background: #F5F5F5;
  color: #1A1A1A;
  border-radius: 16px 16px 16px 4px;
  text-align: right;
  direction: rtl;
  box-shadow: none;
  border: none;
}

.message-bubble-support.message-bubble-grouped {
  border-top-left-radius: 4px;
}

/* User message bubble: CSS custom properties for easy theming */
.message-bubble-user {
  --user-message-bg: #5B7CFA;
  --user-message-shadow: 0 2px 6px rgba(91, 124, 250, 0.2);
  --user-timestamp-color: #E0E0E0;
  --user-checkmark-color: #C7E0FF;
  background: var(--user-message-bg);
  color: #FFFFFF;
  border-radius: 16px 16px 4px 16px;
  text-align: right;
  direction: rtl;
  box-shadow: var(--user-message-shadow);
  border: none;
  position: relative;
  padding: 14px 16px;
}

.message-bubble-user.message-bubble-grouped {
  border-top-right-radius: 4px;
}

.message-bookmark-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  animation: bookmarkFadeIn 0.2s ease-out;
  z-index: 1;
}

/* RTL Support */
[dir="rtl"] .message-bookmark-btn {
  right: auto;
  left: 8px;
}

@keyframes bookmarkFadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.message-bookmark-btn:hover {
  background: #FFFFFF;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: scale(1.1);
}

.message-bookmark-btn:active {
  transform: scale(0.95);
}

.message-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.01em;
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;
}

.message-link {
  color: #4CAF50 !important;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;
  word-break: break-all;
}

.message-link:hover {
  color: #45A049 !important;
  text-decoration: underline;
}

.message-text--user .message-link,
.message-bubble-user .message-link {
  color: #81C784 !important;
  text-decoration: underline;
}

.message-text--user .message-link:hover,
.message-bubble-user .message-link:hover {
  color: #A5D6A7 !important;
  text-decoration: underline;
}

.message-bot-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #666666;
  margin-bottom: 6px;
}

.message-media {
  margin-top: 8px;
  border-radius: 8px;
  overflow: hidden;
}

.message-media img {
  max-width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  transition: opacity 0.3s ease;
}

.lazy-image {
  opacity: 0.3;
  background: #f0f0f0;
  min-height: 100px;
}

.lazy-image.loaded {
  opacity: 1;
  background: transparent;
}

.link-preview {
  display: block;
  text-decoration: none;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
  transition: all 0.2s ease;
}

.link-preview:hover {
  border-color: #5B7CFA;
  box-shadow: 0 2px 8px rgba(91, 124, 250, 0.1);
}

.link-preview-content {
  direction: rtl;
  text-align: right;
}

.link-preview-title {
  font-size: 14px;
  font-weight: 600;
  color: #1A1A1A;
  margin-bottom: 4px;
}

.link-preview-description {
  font-size: 12px;
  font-weight: 400;
  color: #666666;
  margin-bottom: 4px;
  line-height: 1.4;
}

.link-preview-url {
  font-size: 11px;
  font-weight: 400;
  color: #5B7CFA;
  word-break: break-all;
}

.message-actions {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  gap: 4px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  padding: 4px;
  animation: actionsFadeIn 0.2s ease-out;
}

@keyframes actionsFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-action-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.message-action-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.message-system {
  display: flex;
  justify-content: center;
  margin: 16px 0;
}

.system-message {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #FAFAFA;
  border: 1px solid #E0E0E0;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 400;
  color: #666666;
  direction: rtl;
}

/* Footer (timestamp + status) is in MessageFooter component */

/* Legacy meta (kept for any edge cases) */
.message-meta,
.message-meta-user {
  display: none;
}

/* RTL Support - user right, support left; timestamps aligned */
[dir="rtl"] .message-user-container {
  margin-left: 0;
  margin-right: auto;
}

[dir="rtl"] .message-support-container {
  margin-right: 0;
  margin-left: auto;
}

[dir="rtl"] .message-meta-user {
  flex-direction: row;
  justify-content: flex-end;
}

[dir="ltr"] .message-bubble-support {
  border-radius: 4px 16px 16px 16px;
}

[dir="ltr"] .message-bubble-support.message-bubble-grouped {
  border-top-right-radius: 4px;
}

[dir="ltr"] .message-bubble-user {
  border-radius: 16px 4px 16px 16px;
}

[dir="ltr"] .message-bubble-user.message-bubble-grouped {
  border-top-left-radius: 4px;
}

/* Responsive adjustments - Mobile */
@media (max-width: 767px) {
  .message-container {
    width: 100%;
    max-width: 100%;
  }

  .message-content-wrapper {
    max-width: 85%;
  }
  
  .message-text {
    font-size: 15px;
  }
  
  .message-bubble {
    padding: 14px;
  }

  .message-bubble-user {
    padding: 14px 16px;
  }

  .message-action-btn {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }

  /* Touch-friendly spacing */
  .message-wrapper {
    margin-bottom: 8px;
  }

  .message-wrapper:not(.message-grouped) {
    margin-bottom: 16px;
  }
}

/* Tablet optimizations */
@media (min-width: 768px) and (max-width: 1024px) {
  .message-action-btn {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
  }
}

/* Accessibility - High contrast mode support */
@media (prefers-contrast: high) {
  .message-bubble-support {
    background: #e5e7eb;
    border: 2px solid #d1d5db;
    color: #111827;
  }
  
  .message-bubble-user {
    background: #5B7CFA;
    border: 2px solid #5B7CFA;
    color: #FFFFFF;
  }

  .message-action-btn {
    border: 2px solid currentColor;
  }
}

/* Focus indicators */
.message-action-btn:focus {
  outline: 2px solid #5B7CFA;
  outline-offset: 2px;
}

.message-action-btn:focus-visible {
  outline: 3px solid #5B7CFA;
  outline-offset: 2px;
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .message-wrapper,
  .message-bubble,
  .message-action-btn {
    animation: none;
    transition: none;
  }
}

/* Message grouping spacing */
.message-item.message-grouped {
  margin-bottom: 2px;
}
</style>